<template>
  <hit-container
    :attributes="trackerAttributes"
    route="tracker"
    :filter-id="trackerId"
    redirect-to-if-empty="trackerList"
    @update-model="initTracker"
  >
    <template #default="trackerContainer">
      <admin-entity-canvas>
        <template #floating-panel>
          <hit-floating-panel
            :navigation-items="navigationItems"
            :selected-instance="tracker?.fullDesignation"
            :data-object-class="TrackerClass"
          />
        </template>
        <template #body>
          <hit-panel
            :resizable="false"
            panel-id="tracker.detail"
            :title="t('hit-admin.tracker.detail')"
          >
            <template #body="{largeEnoughForGrid}">
              <div class="hit-form">
                <hit-group-input
                  :label="t('hit-admin.tracker.serial-no')"
                  :large-enough-for-group="largeEnoughForGrid"
                  input-distribution="1fr 0.4fr 1fr 0.4fr 1fr"
                >
                  <hit-container-input
                    :inline-input="largeEnoughForGrid"
                    hide-label
                    inline-label-color-visible
                    :container="trackerContainer"
                    attribute="serialNo"
                    :instant-focus="instantFocusSerialNo"
                  />
                  <hit-container-input
                    :inline-input="largeEnoughForGrid"
                    inline-label-color-visible
                    :container="trackerContainer"
                    attribute="imei"
                  />
                  <hit-container-input
                    :inline-input="largeEnoughForGrid"
                    inline-label-color-visible
                    :container="trackerContainer"
                    attribute="simNo"
                  />
                </hit-group-input>
                <hit-group-input
                  :label="t('hit-admin.tracker.model')"
                  :large-enough-for-group="largeEnoughForGrid"
                  input-distribution="1fr 0.4fr 1fr 0.4fr 1fr"
                >
                  <hit-container-enum-label-select
                    hide-label
                    inline-input
                    validate
                    :container="trackerContainer"
                    :static-values="modelOptions"
                    attribute="model"
                  />
                  <hit-container-enum-label-select
                    :label="t('hit-admin.tracker.category')"
                    inline-input
                    validate
                    :container="trackerContainer"
                    :static-values="categoryOptions"
                    attribute="category"
                  />
                  <hit-container-enum-label-select
                    :label="t('hit-admin.tracker.badging-type')"
                    inline-input
                    validate
                    :container="trackerContainer"
                    :static-values="badgingTypeOptions"
                    attribute="badgingType"
                  />
                </hit-group-input>
                <!-- Do you want to add the last send message input? -->
                <!-- <hit-container-input
                  :container="trackerContainer"
                  attribute="lastSendMessage"
                /> -->
                <hit-container-input-checkbox
                  :container="trackerContainer"
                  attribute="active"
                />
              </div>
              <hit-button
                :label="t('hit-admin.tracker.memo')"
                color="success"
                prefix-icon="edit"
                @click="openMemoEditor"
              />
              <hit-modal
                v-if="openMemoModal"
                :title="t('hit-admin.tracker.memo')"
                prefix-icon="edit"
                @cancel="openMemoModal = false"
              >
                <hit-input-rich-text
                  :value="memoDraft"
                  @input="(v) => (memoDraft = v)"
                />
                <div class="flex justify-end gap-2 mt-4">
                  <hit-button
                    :label="t('hit-admin.common.cancel')"
                    color="warning"
                    prefix-icon="close"
                    @click="openMemoModal = false"
                  />
                  <hit-button
                    :label="t('hit-admin.common.save')"
                    color="success"
                    prefix-icon="save"
                    @click="handleSaveMemo(trackerContainer)"
                  />
                </div>
              </hit-modal>
            </template>
          </hit-panel>
          <hit-panel
            :resizable="false"
            panel-id="tracker.link-to-company"
            :title="t('hit-admin.tracker.link-to-company')"
          >
            <template #body>
              <div class="hit-form">
                <hit-select
                  :label="t('hit-admin.tracker.company')"
                  :options="companyOptions"
                  :clearable="true"
                  :label-fields="['designation']"
                  :value="linkedCompanyId"
                  @change="handleCompanyChange"
                />
              </div>
            </template>
          </hit-panel>
        </template>
      </admin-entity-canvas>
    </template>
  </hit-container>
</template>

<script>
import {defineComponent} from 'vue';
import AdminEntityCanvas from '../../components/hit/AdminEntityCanvas.vue';
import HitFloatingPanel from '@/components/hit/navigation/HitFloatingPanel.vue';
import {HitPanel, HitInputRichText, HitSelect} from '@hit/components';
import {Tracker, DataService} from '@hit/base';
import {
  HitContainer,
  HitContainerInput,
  HitContainerEnumLabelSelect,
  HitContainerInputCheckbox,
} from '@hit/base/src/components';
import {HitRouteUtils} from '../../utils/routes';
import {useI18n} from 'vue-i18n';
import {HitAdminUiEnums} from '../../helpers/constants';

export default defineComponent({
  name: 'TrackerDetail',
  components: {
    AdminEntityCanvas,
    HitFloatingPanel,
    HitPanel,
    HitContainer,
    HitContainerInput,
    HitContainerEnumLabelSelect,
    HitContainerInputCheckbox,
    HitInputRichText,
    HitSelect,
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      tracker: null,
      linkedCompanyId: null,
      openMemoModal: false,
      memoDraft: '',
      trackerAttributes: Tracker.allAttributes,
      modelOptions: HitAdminUiEnums.getEnumValues('MODEL', this.t),
      categoryOptions: HitAdminUiEnums.getEnumValues('CATEGORY', this.t),
      badgingTypeOptions: HitAdminUiEnums.getEnumValues('BADGING_TYPE', this.t),
      companyOptions: [],
    };
  },
  computed: {
    trackerId() {
      return this.$route.params.id;
    },
    isNewItem() {
      return this.$route.query.newItem;
    },
    instantFocusSerialNo() {
      return this.isNewItem;
    },
    TrackerClass() {
      return Tracker;
    },
    navigationItems() {
      return [
        HitRouteUtils.getEntityListRoute('tracker'),
        HitRouteUtils.getCurrentRoute(this.tracker?.fullDesignation ?? ''),
      ];
    },
  },
  methods: {
    initTracker(data) {
      this.tracker = new Tracker(data);
    },
    openMemoEditor() {
      this.memoDraft = this.tracker?.memo ?? '';
      this.openMemoModal = true;
    },
    handleSaveMemo(trackerContainer) {
      trackerContainer.handleChange('memo', this.memoDraft);
      this.openMemoModal = false;
    },
    getLinkedCompany() {
      DataService.read('tracker_company', {
        attributes: 'company_id(id,designation)',
        filters: {
          tracker_id: 'eq.' + this.trackerId,
        },
      }).then((response) => {
        this.linkedCompanyId = response.data[0]?.company_id?.id;
      });
    },
    handleCompanyChange(companyId) {
      const hadExistingLink = !!this.linkedCompanyId;
      this.linkedCompanyId = companyId;
      const trackerCompanyFilters = {
        tracker_id: 'eq.' + this.trackerId,
      };

      if (!this.linkedCompanyId) {
        return DataService.delete(
          'tracker_company',
          trackerCompanyFilters
        ).then(() => {
          this.linkedCompanyId = null;
        });
      }

      if (hadExistingLink) {
        return DataService.update('tracker_company', trackerCompanyFilters, {
          company_id: companyId,
        }).catch((error) => {
          throw error;
        });
      }

      return DataService.create('tracker_company', {
        tracker_id: this.trackerId,
        company_id: companyId,
      })
        .then(() => this.getLinkedCompany())
        .catch((error) => {
          throw error;
        });
    },
    getAllCompanies() {
      DataService.read('company', {
        attributes: 'id, designation',
        filters: {
          active: 'eq.true',
        },
      }).then((response) => {
        this.companyOptions = response.data.map((company) => ({
          designation: company.designation,
          id: company.id,
        }));
      });
    },
  },
  mounted() {
    this.getLinkedCompany();
    this.getAllCompanies();
  },
});
</script>

<style lang="scss" scoped></style>
